import React from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { Spa } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export const GuidedQuizSetup = ({
  value,
  onChange,
  initial = {},
  busy,
  onSubmit,
}) => {
  const state = value || {
    step: 0,
    topic: initial.topic || "",
    focus: "",
    due_date: initial.due_date || "",
    number_of_mcq: 0,
    number_of_true_false: 0,
  };
  const update = (patch) => onChange({ ...state, ...patch });
  const topicInstructions = state.focus?.trim()
    ? `${state.topic.trim()}\n\nParticular areas of focus: ${state.focus.trim()}`
    : state.topic.trim();
  const detailsValid =
    Boolean(state.topic.trim()) &&
    topicInstructions.length <= 6000 &&
    Boolean(state.due_date) &&
    dayjs(state.due_date).isValid() &&
    !dayjs(state.due_date).isBefore(dayjs().startOf("day"));
  const counts = [state.number_of_mcq, state.number_of_true_false];
  const total = counts.reduce((sum, count) => sum + Number(count), 0);
  const countsValid =
    counts.every((count) => Number.isInteger(count) && count >= 0) &&
    total >= 2 &&
    total <= 50;
  return (
    <Stack spacing={2} sx={{ my: 2 }}>
      {state.step === 0 ? (
        <>
          <Typography>What’s the quiz about, and when is it due?</Typography>
          <TextField
            label="Quiz topic"
            value={state.topic}
            onChange={(event) => update({ topic: event.target.value })}
            multiline
            minRows={2}
            disabled={busy}
            fullWidth
            helperText="Describe the topic and anything you would like Bud to cover."
          />
          <TextField
            label="Particular areas of focus (optional)"
            value={state.focus || ""}
            onChange={(event) => update({ focus: event.target.value })}
            multiline
            minRows={2}
            disabled={busy}
            fullWidth
            helperText={
              topicInstructions.length > 6000
                ? "Keep the topic and focus within 6,000 characters in total."
                : "Any specific themes, skills or misconceptions you would like covered."
            }
            error={topicInstructions.length > 6000}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due date"
              value={state.due_date ? dayjs(state.due_date) : null}
              onChange={(date) =>
                update({
                  due_date: date?.isValid() ? date.format("YYYY-MM-DD") : "",
                })
              }
              disablePast
              format="DD/MM/YYYY"
              disabled={busy}
              slotProps={{
                textField: { size: "small" },
                openPickerButton: { sx: { border: "none" } },
              }}
            />
          </LocalizationProvider>
          <Button
            disabled={busy || !detailsValid}
            onClick={() => update({ step: 1 })}
          >
            Continue
          </Button>
        </>
      ) : (
        <>
          <Typography>How many questions would you like?</Typography>
          {[
            ["number_of_mcq", "Multiple-choice questions"],
            ["number_of_true_false", "True/false questions"],
          ].map(([key, label]) => (
            <TextField
              key={key}
              label={label}
              type="number"
              value={state[key]}
              disabled={busy}
              onChange={(event) =>
                update({
                  [key]:
                    event.target.value === "" ? "" : Number(event.target.value),
                })
              }
              slotProps={{
                htmlInput: { min: 0, max: 50, step: 1 },
                inputLabel: { shrink: true },
              }}
              fullWidth
            />
          ))}
          <Typography variant="body2">
            Total: {total} questions. Choose 2–50 in total; either type can be
            zero.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button disabled={busy} onClick={() => update({ step: 0 })}>
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<Spa />}
              disabled={busy || !detailsValid || !countsValid}
              onClick={() =>
                onSubmit({
                  ...initial,
                  kind: "quiz",
                  guided_quiz: true,
                  name: (initial.name || state.topic.trim()).slice(0, 200),
                  topic: topicInstructions,
                  due_date: state.due_date,
                  number_of_mcq: state.number_of_mcq,
                  number_of_true_false: state.number_of_true_false,
                })
              }
            >
              Generate with Bud
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
